import {
  fetchNewsCandidates,
  rankNewsStories,
  type AggregatedNewsFeed,
} from '../services/newsFeedService';
import type { CursorAgentProgress } from '../services/cursorAgentApi';
import { escapeAttr, escapeHtml } from '../utils/escapeHtml';
import { loadNewsPreferences } from '../utils/newsPreferences';
import type { NewsStory, RankedNewsStory } from '../utils/newsStory';
import { newsUserFacingText } from '../utils/newsUserFacingText';

type NewsViewCache = {
  feed: AggregatedNewsFeed;
  blocklistInvalidLines: string[];
  candidates: NewsStory[];
};

/** Reddit + publisher RSS; headlines show immediately, AI rank is optional. */
export class NewsView {
  private abort: AbortController | null = null;
  private rankAbort: AbortController | null = null;
  private cache: NewsViewCache | null = null;
  /** Reload database/.news-feed.json from GitHub (Refresh only; set by appViews.showNews). */
  reloadConfig: (() => Promise<void>) | null = null;

  render(container: HTMLElement, options: { refresh?: boolean } = {}): void {
    const forceFetch = options.refresh === true;

    this.abort?.abort();
    this.abort = new AbortController();
    const signal = this.abort.signal;

    container.innerHTML = `
      <div class="news-page">
        <header class="news-page-header news-page-header-compact">
          <h2 class="file-title">News</h2>
          <div class="news-page-actions">
            <button type="button" class="btn-ghost-dark" id="news-ai-rank">AI Rank</button>
            <button type="button" class="btn-primary" id="news-refresh">Refresh</button>
          </div>
        </header>
        <p class="news-rank-status text-muted small hidden" id="news-rank-status" role="status"></p>
        <div class="news-page-body" id="news-page-body"${forceFetch || !this.cache ? ' aria-busy="true"' : ''}>
          ${
            !forceFetch && this.cache
              ? this.bodyHtml(this.cache.feed, this.cache.blocklistInvalidLines)
              : `<div class="news-loading">
            <span class="app-spinner" aria-hidden="true"></span>
            <p id="news-loading-status">Fetching feeds…</p>
          </div>`
          }
        </div>
      </div>`;

    const body = container.querySelector<HTMLElement>('#news-page-body')!;
    const rankStatus = container.querySelector<HTMLElement>('#news-rank-status')!;
    const aiRankBtn = container.querySelector<HTMLButtonElement>('#news-ai-rank')!;

    container.querySelector('#news-refresh')?.addEventListener('click', () => {
      void (async () => {
        await this.reloadConfig?.();
        this.render(container, { refresh: true });
      })();
    });

    aiRankBtn.addEventListener('click', () => {
      void this.rankWithAi(body, rankStatus, aiRankBtn);
    });

    this.syncAiRankButton(aiRankBtn);

    if (!forceFetch && this.cache) {
      body.removeAttribute('aria-busy');
      return;
    }

    void this.load(body, signal);
  }

  private syncAiRankButton(btn: HTMLButtonElement): void {
    btn.disabled = !this.cache || this.cache.candidates.length === 0;
  }

  private setLoadingStatus(body: HTMLElement, text: string): void {
    const el = body.querySelector<HTMLElement>('#news-loading-status');
    if (el) el.textContent = text;
  }

  private setRankStatus(el: HTMLElement, text: string | null): void {
    if (!text) {
      el.textContent = '';
      el.classList.add('hidden');
      return;
    }
    el.textContent = text;
    el.classList.remove('hidden');
  }

  private formatRankProgress(progress: CursorAgentProgress): string {
    if (progress.phase === 'poll' && progress.status) {
      const secs = (progress.poll ?? 0) * 2;
      return `Ranking with AI… ${progress.status} (${secs}s)`;
    }
    if (progress.phase === 'create') return 'Ranking with AI… starting';
    if (progress.phase === 'fetch-agent') return 'Ranking with AI… loading';
    return 'Ranking with AI…';
  }

  private bodyHtml(feed: AggregatedNewsFeed, blocklistInvalid: string[]): string {
    return this.warningsHtml(feed, blocklistInvalid) + this.storiesSectionHtml(feed);
  }

  private paintBody(
    body: HTMLElement,
    feed: AggregatedNewsFeed,
    blocklistInvalid: string[]
  ): void {
    body.innerHTML = this.bodyHtml(feed, blocklistInvalid);
    body.removeAttribute('aria-busy');
  }

  private async load(body: HTMLElement, signal: AbortSignal): Promise<void> {
    const prefs = loadNewsPreferences();

    if (!body.querySelector('.news-loading')) {
      body.setAttribute('aria-busy', 'true');
      body.innerHTML = `
          <div class="news-loading">
            <span class="app-spinner" aria-hidden="true"></span>
            <p id="news-loading-status">Fetching feeds…</p>
          </div>`;
    }

    try {
      const { candidates, blocklistInvalidLines } = await fetchNewsCandidates(prefs, signal);
      if (signal.aborted) return;

      const feed: AggregatedNewsFeed = {
        ranked: candidates.slice(0, prefs.maxRankedStories),
        blocklistInvalidLines,
        aiRanked: false,
      };
      this.cache = { feed, blocklistInvalidLines, candidates };
      this.paintBody(body, feed, blocklistInvalidLines);

      const aiBtn = body.closest('.news-page')?.querySelector<HTMLButtonElement>('#news-ai-rank');
      if (aiBtn) this.syncAiRankButton(aiBtn);
    } catch (err) {
      if (signal.aborted) return;
      body.innerHTML = `
        <div class="news-error">
          <p>Could not load news: ${escapeHtml(newsUserFacingText((err as Error).message))}</p>
          <p class="text-muted small">Try Refresh.</p>
        </div>`;
      body.removeAttribute('aria-busy');
    }
  }

  private async rankWithAi(
    body: HTMLElement,
    rankStatus: HTMLElement,
    aiRankBtn: HTMLButtonElement
  ): Promise<void> {
    if (!this.cache || this.cache.candidates.length === 0) return;

    this.rankAbort?.abort();
    this.rankAbort = new AbortController();
    const signal = this.rankAbort.signal;
    const prefs = loadNewsPreferences();

    aiRankBtn.disabled = true;
    this.setRankStatus(rankStatus, 'Ranking with AI…');

    try {
      const { ranked, rankError } = await rankNewsStories(this.cache.candidates, prefs, {
        signal,
        onRankProgress: progress => {
          if (signal.aborted) return;
          this.setRankStatus(rankStatus, this.formatRankProgress(progress));
        },
      });
      if (signal.aborted) return;

      this.cache.feed = {
        ranked,
        blocklistInvalidLines: this.cache.blocklistInvalidLines,
        aiRanked: !rankError,
        ...(rankError ? { rankError } : {}),
      };
      this.paintBody(body, this.cache.feed, this.cache.blocklistInvalidLines);
      this.setRankStatus(rankStatus, null);
    } catch (err) {
      if (signal.aborted) return;
      const msg = newsUserFacingText((err as Error).message);
      this.setRankStatus(rankStatus, `AI ranking failed: ${msg}`);
    } finally {
      aiRankBtn.disabled = false;
      this.syncAiRankButton(aiRankBtn);
    }
  }

  private warningsHtml(feed: AggregatedNewsFeed, blocklistInvalid: string[]): string {
    const parts: string[] = [];
    if (blocklistInvalid.length > 0) {
      parts.push(
        `<p class="news-warn">Invalid blocklist regex skipped: ${escapeHtml(blocklistInvalid.join(', '))}</p>`
      );
    }
    if (feed.rankError) {
      parts.push(
        `<p class="news-warn">AI ranking failed (${escapeHtml(newsUserFacingText(feed.rankError))}). Showing unranked headlines.</p>`
      );
    }
    return parts.join('');
  }

  private storiesSectionHtml(feed: AggregatedNewsFeed): string {
    if (feed.ranked.length === 0) {
      return `<p class="news-empty text-muted">No stories right now.</p>`;
    }

    const items = feed.ranked.map((s, i) => this.storyItemHtml(s, i + 1)).join('');
    return `<ol class="news-list news-list-ranked" aria-label="News">${items}</ol>`;
  }

  private storyItemHtml(story: RankedNewsStory, index: number): string {
    const href = story.link || '#';
    return `<li class="news-item news-item-ranked">
      <span class="news-item-index">${index}</span>
      <div class="news-item-body">
        <a class="news-item-link" href="${escapeAttr(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(story.title)}</a>
        <span class="news-item-meta">
          <span class="news-item-source">${escapeHtml(story.sourceLabel)}</span>
        </span>
      </div>
    </li>`;
  }
}
